import Parameter from "../../../api/models/test-series/Parameter";
import ParameterValue from "../../../api/models/test-series/ParameterValue";
import NumberParameter from "../../../api/models/test-series/NumberParameter";
import NumberParameterValue from "../../../api/models/test-series/NumberParameterValue";
import EnumParameter from "../../../api/models/test-series/EnumParameter";
import EnumParameterValue from "../../../api/models/test-series/EnumParameterValue";
import TextParameterValue from "../../../api/models/test-series/TextParameterValue";

export interface ParameterValueModel {
  id: string;
  name: string;
  parameterValueId?: number;
  value: string;
  type: "NUMBER" | "ENUM" | "TEXT";
  parameter: Parameter;
  uncontrolledId: string;
  // include indicates that the row is not uncontrolled and will be included in the test request
  include: boolean;
}

export interface NumberParameterValueModel extends ParameterValueModel {
  maxValue: string;
  maxValueId: string;
  minValue: string;
  minValueId: string;
  unitId: string;
  unitCode: string;
  units: string[];
}

export interface EnumParameterValueModel extends ParameterValueModel {
  values: string[];
}

export type TextParameterValueModel = ParameterValueModel;

export function handleParameter<T>(
  parameterValue: ParameterValueModel,
  decision: {
    enum: (value: EnumParameterValueModel) => T;
    number: (value: NumberParameterValueModel) => T;
    text: (value: TextParameterValueModel) => T;
  },
): T {
  if (parameterValue.type === "NUMBER") {
    return decision.number(parameterValue as NumberParameterValueModel);
  }
  if (parameterValue.type === "ENUM") {
    return decision.enum(parameterValue as EnumParameterValueModel);
  }
  if (parameterValue.type === "TEXT") {
    return decision.text(parameterValue as TextParameterValueModel);
  }
  throw new Error("illegal state");
}

function sortByKey<T extends Record<string, string | number>, K extends keyof T>(key: K, a: T, b: T) {
  if (a[key] > b[key]) {
    return 1;
  }
  if (a[key] < b[key]) {
    return -1;
  }
  return 0;
}

export function mapToModel(index: number, parameter: Parameter, value?: ParameterValue): ParameterValueModel {
  const prefix = `ParameterListDialog_row`;
  if (parameter.type === "NUMBER") {
    const numberParameter = parameter as NumberParameter;
    const scalingsSorted = [...numberParameter.unit.scalings].sort((scalA, scalB) =>
      scalA.scaling === scalB.scaling ? sortByKey("code", scalA, scalB) : sortByKey("scaling", scalA, scalB),
    );
    const units = scalingsSorted.map((s) => s.code);
    const defaultUnit = units[0];
    const numberParameterValue = value as NumberParameterValue;
    const numberModel: NumberParameterValueModel = {
      id: `${prefix}-${index}-value`,
      type: parameter.type,
      parameterValueId: value?.id,
      value: value?.uncontrolled ? "" : value ? numberParameterValue.value?.toString() || "" : "",
      minValue: value?.uncontrolled ? "" : value ? numberParameterValue.minValue?.toString() || "" : "",
      minValueId: `${prefix}-${index}-minValue`,
      maxValue: value?.uncontrolled ? "" : value ? numberParameterValue.maxValue?.toString() || "" : "",
      maxValueId: `${prefix}-${index}-maxValue`,
      name: numberParameter.name,
      unitId: `${prefix}-${index}-unit`,
      unitCode: value ? numberParameterValue.unitCode || defaultUnit : defaultUnit,
      units,
      parameter,
      uncontrolledId: value?.action ? `${prefix}-${index}-include` : `${prefix}-${index}-exclude`,
      include: !value?.uncontrolled,
    };
    return numberModel as ParameterValueModel;
  }
  if (parameter.type === "ENUM") {
    const enumParameter = parameter as EnumParameter;
    const defaultValue = enumParameter.values[0];
    const enumModel: EnumParameterValueModel = {
      id: `${prefix}-${index}-value`,
      type: parameter.type,
      parameterValueId: value?.id,
      value: value?.uncontrolled ? "" : value ? (value as EnumParameterValue).value || defaultValue : defaultValue,
      name: enumParameter.name,
      values: enumParameter.values,
      parameter,
      uncontrolledId: value?.action ? `${prefix}-${index}-include` : `${prefix}-${index}-exclude`,
      include: !value?.uncontrolled,
    };
    return enumModel as ParameterValueModel;
  }
  if (parameter.type === "TEXT") {
    const textParameter = parameter as Parameter;
    return {
      id: `${prefix}-${index}-value`,
      type: parameter.type,
      parameterValueId: value?.id,
      value: value?.uncontrolled ? "" : value ? (value as TextParameterValue).value || "" : "",
      name: textParameter.name,
      parameter,
      uncontrolledId: value?.action ? `${prefix}-${index}-include` : `${prefix}-${index}-exclude`,
      include: !value?.uncontrolled,
    };
  }
  throw Error("illegal state");
}
